import { action, observable, makeObservable, computed } from 'mobx';

import AuthSettingsStore from '../AuthSettingsStore';
import {
    MapStorageTypeToSettingsFields,
    SettingsResponse,
    IntegrationType,
    ConfiguredIntegrations,
    StorageType,
    ConnectorType,
    ConnectorHealthStatus,
    ConnectorHealthResponse,
    PurviewLabel,
    SharepointKeysStatus,
} from './interfaces';
import { BASEURL, ENDPOINTS } from '../../api';
import { adminJwtGet, adminJwtPost } from '../../api/helpers/adminJwtRequest';
import { captureErrorForSentry } from '../../components/utils';

/** All supported storage integrations */
export const STORAGE_TYPES: StorageType[] = ['sharepoint', 'gcs'];

/** All supported connector integrations */
export const CONNECTOR_TYPES: ConnectorType[] = ['slack', 'salesforce', 'purview', 'sharepoint_connector'];

/** All integration types that can be configured via API keys */
export const ALL_INTEGRATION_TYPES: IntegrationType[] = [...STORAGE_TYPES, ...CONNECTOR_TYPES];

/** Default field definitions for each integration type (fallback when backend returns empty) */
export const DEFAULT_INTEGRATION_FIELDS: Record<IntegrationType, string[] | 'file'> = {
    sharepoint: ['site', 'clientId', 'realm'],
    gcs: 'file', // GCS uses JSON file upload
    slack: ['clientId', 'clientSecret', 'signingSecret', 'botToken', 'userToken'],
    salesforce: ['loginUrl', 'clientId', 'clientSecret'],
    purview: ['tenantId', 'clientId', 'clientSecret'],
    sharepoint_connector: ['tenantId', 'clientId', 'clientSecret'],
};

class SettingsStore {
    constructor(authSettingsStore: AuthSettingsStore) {
        this.authSettingsStore = authSettingsStore;
        makeObservable(this);
    }

    private readonly authSettingsStore: AuthSettingsStore;

    /** @deprecated Use ALL_INTEGRATION_TYPES instead */
    storagesTypes: string[] = ALL_INTEGRATION_TYPES;

    /** Field definitions for each integration type */
    integrationFields: MapStorageTypeToSettingsFields = {};

    /** Currently configured integrations from backend */
    @observable configuredIntegrations: ConfiguredIntegrations = {};

    @observable isLoading = false;

    /** Currently selected integration type for new config */
    @observable selectedIntegrationType: IntegrationType | null = null;

    /** @deprecated Legacy support */
    defaultStoragesObjects: MapStorageTypeToSettingsFields = {};

    /** @deprecated Legacy support */
    @observable newStorageType: IntegrationType = 'sharepoint';

    /** @deprecated Legacy support */
    @observable newStorageObject: Record<string, string> = {};

    @observable connectorHealthStatus: Partial<Record<ConnectorType, ConnectorHealthStatus>> = {};

    @observable purviewLabels: PurviewLabel[] = [];

    @observable isPurviewLabelsLoading = false;

    @observable isPurviewRescanning = false;

    @observable sharepointKeysStatus: SharepointKeysStatus | null = null;

    @observable isSharepointKeysLoading = false;

    @observable isSharepointKeysGenerating = false;

    /** Get configured storage type (only one allowed) */
    @computed
    get configuredStorage(): StorageType | null {
        for (const storageType of STORAGE_TYPES) {
            if (this.configuredIntegrations[storageType]) {
                return storageType;
            }
        }
        return null;
    }

    @computed
    get configuredConnectors(): ConnectorType[] {
        return CONNECTOR_TYPES.filter((type) => !!this.configuredIntegrations[type]);
    }

    /** Check if a specific integration is fully configured (all fields present) */
    isIntegrationConfigured = (type: IntegrationType): boolean => {
        const values = this.configuredIntegrations[type];
        if (!values || typeof values !== 'object') return false;

        const fields = this.getIntegrationFields(type);
        // For file-upload integrations, any stored value counts
        if (fields === 'file') return Object.keys(values).length > 0;
        // For field-based integrations, every field must have a non-empty value
        if (Array.isArray(fields) && fields.length > 0) {
            return fields.every((field) => {
                const val = values[field];
                return typeof val === 'string' && val.trim() !== '';
            });
        }
        return false;
    };

    /** Get field definitions for an integration type */
    getIntegrationFields = (type: IntegrationType): string[] | 'file' => {
        const fields = this.integrationFields[type];
        if (fields === 'file' || typeof fields === 'string') {
            return 'file';
        }
        // Use backend fields if available, otherwise fall back to defaults
        if (fields && fields.length > 0) {
            return fields;
        }
        return DEFAULT_INTEGRATION_FIELDS[type] || [];
    };

    /** Get current values for an integration, with initial empty values if not configured */
    getIntegrationValues = (type: IntegrationType): Record<string, string> => {
        const configuredValues = this.configuredIntegrations[type];
        if (configuredValues && Object.keys(configuredValues).length > 0) {
            return configuredValues;
        }

        // Return initial empty values based on field definitions (with fallback to defaults)
        const fields = this.getIntegrationFields(type);
        if (fields && Array.isArray(fields)) {
            const initialValues: Record<string, string> = {};
            fields.forEach((field) => {
                initialValues[field] = '';
            });
            return initialValues;
        }

        return {};
    };

    /** Get the health endpoint URL for a connector type, or null if not a connector.
     *  Purview does not expose a health-check endpoint. */
    getConnectorUrl = (type: ConnectorType): string | null => {
        if (type === 'purview' || type === 'sharepoint_connector') return null;
        if (CONNECTOR_TYPES.includes(type)) {
            const endpoint = ENDPOINTS.getConnectorHealth(type);
            return Array.isArray(endpoint) ? endpoint[1] : endpoint;
        }
        return null;
    };

    @action
    setSelectedIntegrationType = (type: IntegrationType | null): void => {
        this.selectedIntegrationType = type;
    };

    @action
    setConfiguredIntegrations = (integrations: ConfiguredIntegrations): void => {
        this.configuredIntegrations = integrations;
    };

    @action
    setIsLoading = (loading: boolean): void => {
        this.isLoading = loading;
    };

    @action
    setNewStorageType = (newType: IntegrationType): void => {
        this.newStorageType = newType;
    };

    @action
    setNewStorageObject = (val: Record<string, string>): void => {
        this.newStorageObject = val;
    };

    clearStorageObject = (): void => {
        const newCleanObject: Record<string, string> = {};
        const newCleanObjectFields = this.defaultStoragesObjects[this.newStorageType];
        if (newCleanObjectFields && Array.isArray(newCleanObjectFields)) {
            newCleanObjectFields.forEach((item) => {
                newCleanObject[item] = '';
            });
        }
        this.setNewStorageObject(newCleanObject);
    };

    setDefaultStorageObjects = (val: MapStorageTypeToSettingsFields): void => {
        this.defaultStoragesObjects = val;
        this.integrationFields = val;
    };

    fetchConfiguredIntegrations = async (): Promise<void> => {
        const { API } = this.authSettingsStore;
        try {
            this.setIsLoading(true);
            const response = await API.get(BASEURL.backend(), ENDPOINTS.getApiSettings(), {});
            this.setConfiguredIntegrations(response.settings || {});
        } catch (error) {
            captureErrorForSentry(error, 'SettingsStore.fetchConfiguredIntegrations');
        } finally {
            this.setIsLoading(false);
        }
    };

    /** Fetch field definitions for all integration types */
    fetchStorageKeys = async (): Promise<void> => {
        const { API } = this.authSettingsStore;
        try {
            const fieldsObject: MapStorageTypeToSettingsFields = {};
            const allResponses: SettingsResponse[] = await Promise.all(
                ALL_INTEGRATION_TYPES.map((item) => (
                    API.get(BASEURL.backend(), ENDPOINTS.getApiSettingsStorageKeys(item), {})
                )),
            );
            ALL_INTEGRATION_TYPES.forEach((item, index) => {
                fieldsObject[item] = allResponses[index].fields;
            });
            this.setDefaultStorageObjects(fieldsObject);
            this.clearStorageObject();
        } catch (error) {
            captureErrorForSentry(error, 'SettingsStore.fetchStorageKeys');
        }
    };

    saveIntegration = async (type: IntegrationType, values: Record<string, string>): Promise<boolean> => {
        const { API } = this.authSettingsStore;
        try {
            this.setIsLoading(true);
            await API.post(BASEURL.backend(), ENDPOINTS.getApiSettings(), {
                body: { settings: { [type]: values } },
            });
            // Update local state
            this.setConfiguredIntegrations({
                ...this.configuredIntegrations,
                [type]: values,
            });
            return true;
        } catch (error) {
            captureErrorForSentry(error, 'SettingsStore.saveIntegration');
            return false;
        } finally {
            this.setIsLoading(false);
        }
    };

    @action
    setConnectorHealthStatus = (
        type: ConnectorType,
        status: ConnectorHealthStatus,
    ): void => {
        this.connectorHealthStatus = {
            ...this.connectorHealthStatus,
            [type]: status,
        };
    };

    /** Fetch health status from a connector service via settings-service proxy */
    fetchConnectorHealth = async (type: ConnectorType): Promise<ConnectorHealthStatus> => {
        const { API } = this.authSettingsStore;

        this.setConnectorHealthStatus(type, {
            isLoading: true,
            isConfigured: null,
            isReachable: null,
        });

        try {
            const data: ConnectorHealthResponse = await API.get(
                BASEURL.backend(),
                ENDPOINTS.getConnectorHealth(type),
                {},
            );

            const status: ConnectorHealthStatus = {
                isLoading: false,
                isConfigured: data.configured ?? null,
                isReachable: data.reachable ?? true,
                error: data.error,
            };

            this.setConnectorHealthStatus(type, status);
            return status;
        } catch (error) {
            const status: ConnectorHealthStatus = {
                isLoading: false,
                isConfigured: null,
                isReachable: false,
                error: error instanceof Error ? error.message : 'Unknown error',
            };
            this.setConnectorHealthStatus(type, status);
            captureErrorForSentry(error, `SettingsStore.fetchConnectorHealth.${type}`);
            return status;
        }
    };

    fetchPurviewLabels = async (): Promise<void> => {
        const { API } = this.authSettingsStore;
        this.isPurviewLabelsLoading = true;
        try {
            const data = await API.get(
                BASEURL.backend(),
                ENDPOINTS.getPurviewLabels(),
                {},
            );
            if (Array.isArray(data)) {
                this.purviewLabels = data;
            } else if (Array.isArray((data as unknown as Record<string, unknown>)?.labels)) {
                this.purviewLabels = (data as unknown as Record<string, unknown>).labels as PurviewLabel[];
            } else {
                this.purviewLabels = [];
            }
        } catch (error) {
            this.purviewLabels = [];
            captureErrorForSentry(error, 'SettingsStore.fetchPurviewLabels');
        } finally {
            this.isPurviewLabelsLoading = false;
        }
    };

    syncPurviewLabels = async (): Promise<boolean> => {
        const { API } = this.authSettingsStore;
        this.isPurviewRescanning = true;
        try {
            await API.post(
                BASEURL.backend(),
                ENDPOINTS.syncPurviewLabels(),
                {},
            );
            await this.fetchPurviewLabels();
            return true;
        } catch (error) {
            captureErrorForSentry(error, 'SettingsStore.syncPurviewLabels');
            return false;
        } finally {
            this.isPurviewRescanning = false;
        }
    };

    @action
    setSharepointKeysStatus = (status: SharepointKeysStatus | null): void => {
        this.sharepointKeysStatus = status;
    };

    @action
    setIsSharepointKeysLoading = (loading: boolean): void => {
        this.isSharepointKeysLoading = loading;
    };

    @action
    setIsSharepointKeysGenerating = (generating: boolean): void => {
        this.isSharepointKeysGenerating = generating;
    };

    fetchSharepointKeysStatus = async (organizationId?: string): Promise<SharepointKeysStatus | null> => {
        const { API, IS_WSO } = this.authSettingsStore;
        this.setIsSharepointKeysLoading(true);
        try {
            const endpoint = ENDPOINTS.getSharepointKeysStatus(organizationId);
            const data: SharepointKeysStatus = IS_WSO
                ? await API.get(BASEURL.backend(), endpoint, {})
                : await adminJwtGet<SharepointKeysStatus>(endpoint);
            this.setSharepointKeysStatus(data);
            return data;
        } catch (error) {
            captureErrorForSentry(error, 'SettingsStore.fetchSharepointKeysStatus');
            return null;
        } finally {
            this.setIsSharepointKeysLoading(false);
        }
    };

    generateSharepointKeys = async (organizationId?: string): Promise<string | null> => {
        const { API, IS_WSO } = this.authSettingsStore;
        this.setIsSharepointKeysGenerating(true);
        try {
            const endpoint = ENDPOINTS.generateSharepointKeys(organizationId);
            const data: { public_certificate: string } = IS_WSO
                ? await API.post(BASEURL.backend(), endpoint, { body: { account_type: 'client' } })
                : await adminJwtPost<{ public_certificate: string }>(
                    endpoint,
                    { account_type: 'client' },
                );
            const status: SharepointKeysStatus = {
                configured: true,
                public_certificate: data.public_certificate,
            };
            this.setSharepointKeysStatus(status);
            return data.public_certificate;
        } catch (error) {
            captureErrorForSentry(error, 'SettingsStore.generateSharepointKeys');
            return null;
        } finally {
            this.setIsSharepointKeysGenerating(false);
        }
    };

    /** Fetch app manifest from a connector service via settings-service proxy */
    fetchConnectorManifest = async (type: ConnectorType): Promise<string | null> => {
        const { API } = this.authSettingsStore;

        try {
            const data = await API.get(
                BASEURL.backend(),
                ENDPOINTS.getConnectorManifest(type),
                {},
            );

            // Return formatted JSON string for display
            return JSON.stringify(data, null, 4);
        } catch (error) {
            captureErrorForSentry(error, `SettingsStore.fetchConnectorManifest.${type}`);
            return null;
        }
    };

}

export default SettingsStore;
